/* eslint-disable react/prop-types */
import React from 'react'
import {useCompare} from '../../context/compare'
import {
    AspectRatio,
    Box,
    Button,
    Flex,
    SimpleGrid,
    Text,
    useDisclosure,
    useStyleConfig,
} from '@chakra-ui/react'
import {FormattedMessage, useIntl} from 'react-intl'
import CompareModal from './compare-modal'
import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image'
import {CloseIcon, ChevronDown, ChevronUp} from '../../components/icons'
import {getImageForProductTile} from '../../utils/utils'

const ProductSelection = ({product}) => {
    const styles = useStyleConfig('ProductComparePlp')
    const {formatMessage} = useIntl()

    const {removeItem} = useCompare()
    const imgSrc = getImageForProductTile(product)

    return (
        <Flex {...styles.productSelectionContainer}>
            <Flex {...styles.productSelectionSubContainer}>
                <AspectRatio {...styles.productSelectionImage}>
                    <DynamicImage
                        src={`${imgSrc}`}
                        imageProps={{
                            // treat img as a decorative item, we don't need to pass `image.alt`
                            // since it is the same as product name
                            // which can cause confusion for individuals who uses screen readers
                            alt: '',
                            loading: 'lazy',
                        }}
                    />
                </AspectRatio>
                <Box>
                    <Text {...styles.productSelectionName}>
                        {product?.representedProduct?.c_variantIdentifier ||
                            product?.name ||
                            product?.productName}
                    </Text>
                    <Text {...styles.productSelectionId}>{product.productId}</Text>
                </Box>
            </Flex>
            <Button
                onClick={() => removeItem(product)}
                variant="unstyled"
                {...styles.productSelectionClose}
                data-testid="close-icon"
                aria-label={formatMessage({
                    id: 'country.banner.close',
                    defaultMessage: 'Close',
                })}
            >
                <CloseIcon />
            </Button>
        </Flex>
    )
}

const ProductSelectionEmpty = () => {
    const styles = useStyleConfig('ProductComparePlp')

    return (
        <Flex {...styles.productSelectionEmptyContainer}>
            <Text>
                <FormattedMessage
                    id="product_list.compare_products.empty_placeholder"
                    defaultMessage="Product to add"
                />
            </Text>
        </Flex>
    )
}

const CompareBanner = () => {
    const {
        state: {compareItems},
        compareBannerRef,
    } = useCompare()
    const {isOpen, onToggle, onClose} = useDisclosure({defaultIsOpen: true})
    const {formatMessage} = useIntl()
    const styles = useStyleConfig('ProductComparePlp')

    const messages = {
        showCompare: {
            id: 'compare_banner.showCompare',
            defaultMessage: 'Show compared products',
        },
        hideCompare: {
            id: 'compare_banner.hideCompare',
            defaultMessage: 'Hide compared products',
        },
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Escape' && isOpen) {
            e.preventDefault()
            onClose()

            // Move focus back to the product list page
            setTimeout(() => {
                const productListPage =
                    document.querySelector('[data-testid="sf-product-grid"]') ||
                    document.querySelector('.sf-product-grid')

                if (productListPage) {
                    // Temporarily make it focusable
                    productListPage.setAttribute('tabindex', '-1')
                    productListPage.focus()

                    // Remove tabindex after focusing to keep it out of normal tab flow
                    setTimeout(() => {
                        productListPage.removeAttribute('tabindex')
                    }, 100)
                } else {
                    // Fallback: blur the banner
                    compareBannerRef?.current?.blur()
                }
            }, 100)
        }
    }

    return (
        compareItems.length > 0 && (
            <Box
                {...styles.banner}
                ref={compareBannerRef}
                tabIndex={-1}
                data-compare-banner
                onKeyDown={handleKeyDown}
            >
                <Flex {...styles.bannerHeader} onClick={onToggle}>
                    <Text {...styles.bannerHeaderLabel}>
                        <FormattedMessage
                            id="product_list.compare_products.label"
                            defaultMessage="Compare products: ({items}/3)"
                            values={{
                                items: compareItems.length,
                            }}
                        />
                    </Text>
                    {isOpen ? (
                        <Button
                            onClick={onToggle}
                            variant="unstyled"
                            {...styles.bannerHeaderButton}
                            aria-label={formatMessage(messages.hideCompare)}
                        >
                            <ChevronDown boxSize="iconsSize.medium" />
                        </Button>
                    ) : (
                        <Button
                            onClick={onToggle}
                            variant="unstyled"
                            {...styles.bannerHeaderButton}
                            aria-label={formatMessage(messages.showCompare)}
                        >
                            <ChevronUp boxSize="iconsSize.medium" />
                        </Button>
                    )}
                </Flex>
                {isOpen && (
                    <SimpleGrid {...styles.bannerBody}>
                        {compareItems.map((item) => (
                            <ProductSelection key={item.productId} product={item} />
                        ))}
                        {[...Array(3 - compareItems.length)].map((_, index) => (
                            <ProductSelectionEmpty key={index} />
                        ))}

                        <CompareModal />
                    </SimpleGrid>
                )}
            </Box>
        )
    )
}

export default CompareBanner
